import { useState } from 'react';
import { X, User, Bot, Wrench, AlertCircle, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import styles from './ConversationDetail.module.css';
import type { Conversation } from '../../store/chatHistoryStore';
import { toast } from '../../lib/toast';

interface ConversationDetailProps {
  conversation: Conversation;
  widgetName?: string;
  onClose: () => void;
}

function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <button
      type="button"
      className={`${styles.copyBtn} ${className || ''}`}
      onClick={handleCopy}
      title="Copy"
    >
      {copied ? <Check size={12} /> : <Copy size={12} />}
    </button>
  );
}

export default function ConversationDetail({ conversation, widgetName, onClose }: ConversationDetailProps) {
  const messages = conversation.messages || [];
  const [toolsExpanded, setToolsExpanded] = useState(false);

  const getStatusLabel = (status: string) => status.replace(/_/g, ' ');

  const renderContent = (content: any): string => {
    if (typeof content === 'string') return content;
    if (Array.isArray(content)) {
      return content
        .map((block: any) => {
          if (block.type === 'text') return block.text || '';
          if (block.type === 'tool_use') return `[Tool: ${block.name}]`;
          if (block.type === 'tool_result') return block.content || '';
          return JSON.stringify(block);
        })
        .join('\n');
    }
    return JSON.stringify(content);
  };

  const getToolUseBlocks = (content: any) => {
    if (!Array.isArray(content)) return [];
    return content.filter((b: any) => b.type === 'tool_use');
  };

  const buildFullTranscript = (): string => {
    return messages
      .map((msg: any) => {
        if (msg.role === 'tool') {
          try {
            const obj = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
            return `[TOOL RESULT]\n${JSON.stringify(obj, null, 2)}`;
          } catch {
            return `[TOOL RESULT]\n${String(msg.content)}`;
          }
        }
        return `[${msg.role.toUpperCase()}]\n${renderContent(msg.content)}`;
      })
      .join('\n\n');
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerInfo}>
            <h3 className={styles.headerTitle}>Conversation Detail</h3>
            <div className={styles.headerMeta}>
              <span>{new Date(conversation.createdAt).toLocaleString()}</span>
              <span className={styles.headerSep}>|</span>
              <span>{conversation.user?.email || conversation.userId}</span>
              {widgetName && (
                <>
                  <span className={styles.headerSep}>|</span>
                  <span className={styles.headerWidget}>{widgetName}</span>
                </>
              )}
              <span className={styles.headerSep}>|</span>
              <span className={styles.headerStatus}>{getStatusLabel(conversation.status)}</span>
              {conversation.channel && (
                <>
                  <span className={styles.headerSep}>|</span>
                  <span>{conversation.channel}{conversation.drivingMode ? ' (driving)' : ''}</span>
                </>
              )}
              {conversation.llmProvider && (
                <>
                  <span className={styles.headerSep}>|</span>
                  <span>{conversation.llmProvider}{conversation.llmModel ? ` / ${conversation.llmModel}` : ''}</span>
                </>
              )}
              {(conversation.ttsProvider || conversation.sttProvider) && (
                <>
                  <span className={styles.headerSep}>|</span>
                  <span>
                    {[conversation.sttProvider && `STT: ${conversation.sttProvider}`, conversation.ttsProvider && `TTS: ${conversation.ttsProvider}`].filter(Boolean).join(', ')}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className={styles.headerActions}>
            {messages.length > 0 && (
              <button
                type="button"
                className={styles.copyAllBtn}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(buildFullTranscript());
                    toast.success('Conversation copied to clipboard');
                  } catch {
                    toast.error('Failed to copy conversation');
                  }
                }}
                title="Copy entire conversation"
              >
                <Copy size={13} />
                Copy all
              </button>
            )}
            <button className={styles.closeBtn} onClick={onClose}>
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Error message */}
        {conversation.errorMessage && (
          <div className={styles.errorBanner}>
            <AlertCircle size={14} />
            {conversation.errorMessage}
          </div>
        )}

        {/* Messages */}
        <div className={styles.messagesArea}>
          {messages.map((msg: any, idx: number) => {
            if (msg.role === 'system') {
              const text = renderContent(msg.content);
              return (
                <div key={idx} className={styles.systemMsg}>
                  <span className={styles.systemLabel}>System Prompt</span>
                  <div className={styles.copyableBlock}>
                    <p className={styles.systemText}>{text}</p>
                    <CopyButton text={text} className={styles.copyBtnFloating} />
                  </div>
                </div>
              );
            }

            if (msg.role === 'user') {
              const text = renderContent(msg.content);
              return (
                <div key={idx} className={`${styles.message} ${styles.userMessage}`}>
                  <div className={styles.messageIcon}>
                    <User size={14} />
                  </div>
                  <div className={`${styles.messageBubble} ${styles.copyableBlock}`}>
                    <p className={styles.messageText}>{text}</p>
                    <CopyButton text={text} className={styles.copyBtnFloating} />
                  </div>
                </div>
              );
            }

            if (msg.role === 'assistant') {
              const toolUses = getToolUseBlocks(msg.content);
              const textContent = Array.isArray(msg.content)
                ? msg.content.filter((b: any) => b.type === 'text').map((b: any) => b.text).join('')
                : msg.content;

              return (
                <div key={idx} className={`${styles.message} ${styles.assistantMessage}`}>
                  <div className={styles.messageIcon}>
                    <Bot size={14} />
                  </div>
                  <div className={`${styles.messageBubble} ${styles.copyableBlock}`}>
                    {textContent && (
                      <p className={styles.messageText}>{textContent}</p>
                    )}
                    {textContent && <CopyButton text={textContent} className={styles.copyBtnFloating} />}
                    {toolUses.map((tool: any, tIdx: number) => (
                      <div key={tIdx} className={styles.toolUseBlock}>
                        <Wrench size={12} />
                        <span className={styles.toolName}>{tool.name}</span>
                        {tool.input && Object.keys(tool.input).length > 0 && (
                          <div className={styles.copyableBlock}>
                            <pre className={styles.toolInput}>
                              {JSON.stringify(tool.input, null, 2)}
                            </pre>
                            <CopyButton text={JSON.stringify(tool.input, null, 2)} className={styles.copyBtnFloating} />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            if (msg.role === 'tool') {
              let parsed: string;
              try {
                const obj = typeof msg.content === 'string' ? JSON.parse(msg.content) : msg.content;
                parsed = JSON.stringify(obj, null, 2);
              } catch {
                parsed = String(msg.content);
              }

              return (
                <div key={idx} className={`${styles.message} ${styles.toolMessage}`}>
                  <div className={styles.messageIcon}>
                    <Wrench size={14} />
                  </div>
                  <div className={`${styles.messageBubble} ${styles.copyableBlock}`}>
                    <span className={styles.toolResultLabel}>Tool Result</span>
                    <pre className={styles.toolResultContent}>{parsed}</pre>
                    <CopyButton text={parsed} className={styles.copyBtnFloating} />
                  </div>
                </div>
              );
            }

            return null;
          })}
        </div>

        {/* Tool invocations summary */}
        {conversation.toolInvocations.length > 0 && (
          <div className={styles.footer}>
            <div className={styles.footerHeader}>
              <span className={styles.footerLabel}>Tools used:</span>
              <button
                type="button"
                className={styles.footerToggle}
                onClick={() => setToolsExpanded(prev => !prev)}
                title={toolsExpanded ? 'Collapse' : 'Expand'}
              >
                {toolsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </div>
            <div className={`${styles.footerTools} ${toolsExpanded ? styles.footerToolsExpanded : ''}`}>
              {conversation.toolInvocations.map((t, i) => (
                <span
                  key={i}
                  className={`${styles.footerToolChip} ${t.success ? styles.toolSuccess : styles.toolFailed}`}
                >
                  {t.toolName}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
